import {
  envolventeDeAudio,
  verificarAudioDeclarado,
  type AudioDeclaradoError,
  type AudioRecorderResult,
  type ConnectArgs,
  type EnvolventeAudio,
  type SimulacionClient,
  type WebSocketClient,
} from '@alter/api-client';
import type { AudioDeclarado, MiSimulacionResponse, WsAudioBinarioPayload } from '@alter/types';
import {
  CAPAS_VIDA_DEFECTOS,
  VIDA_DEFECTOS,
  type CapasVida,
  type VidaOpts,
} from '../../avatar/vida';
import { tenantIdDelToken } from '../../lib/jwt';
import type { EntradaDiagnostico } from '../../stores/simulacionStore';
import { medirRms, UMBRAL_RMS_SILENCIO, type Decodificador } from './rms';
import {
  DURACION_AVISO_MS,
  ESPERA_AUDIO_PACIENTE_MS,
  ESPERA_CONEXION_MS,
  ESPERA_RECONEXION_MS,
  ESPERA_RESPUESTA_MS,
  pttArmado,
  pttGrabando,
  TOPE_GRABACION_MS,
  turnoEnProceso,
  type EstadoSesion,
  type EventoSesion,
} from './sesionMachine';

/** Lo que el orquestador usa del cliente WS. */
export type WsSesion = Pick<
  WebSocketClient,
  | 'on'
  | 'connect'
  | 'disconnect'
  | 'emitAudioGeneradoPorAlumno'
  | 'emitEntrevistaFinalizadaPorAlumno'
  | 'closeSession'
>;

export type ApiSesion = Pick<SimulacionClient, 'getMiSimulacion' | 'iniciar' | 'finalizar'>;

/** Lo que el orquestador usa del `AudioRecorder`. */
export interface Grabador {
  start(): Promise<void>;
  stop(): Promise<AudioRecorderResult>;
  dispose(): void;
}

/** Lo que el orquestador usa del `AudioPlayer`. */
export interface Reproductor {
  play(binary: ArrayBuffer, mimeType: string): Promise<void>;
  stop(): void;
}

/** Contexto de audio de la app: desbloquea el audio (D-8) y decodifica para el RMS (D-7). */
export interface ContextoAudio extends Decodificador {
  resume(): Promise<void>;
  close(): Promise<void>;
}

export interface Reloj {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(id: unknown): void;
}

/**
 * Lo que el orquestador le pide al avatar. Lo implementa `AvatarStage`
 * sobre el `ThreeRigAdapter`; el orquestador no sabe de three.js ni de
 * blend shapes.
 *
 * El habla (tanda 4, D-4) entra por acá y **no** por la expresión: son
 * canales distintos, porque cualquier expresión puede estar hablando.
 */
export interface ControlAvatar {
  /**
   * @param duracionMs duración de la transición; sin ella, la del motor
   *   (`DEFAULT_TRANSITION_MS`).
   */
  aplicarExpresion(expresion: string, duracionMs?: number): void;
  /** Mueve la boca con la envolvente del audio que está sonando. */
  hablar(envolvente: EnvolventeAudio, desfaseMs?: number): void;
  dejarDeHablar(): void;
  /**
   * El turno entró o salió de proceso (tanda 8, pieza 5): el avatar
   * "piensa". Es la fase del turno, no una emoción.
   */
  pensar(activo: boolean): void;
  /** Empieza el pre-roll: el avatar toma aire antes de que suene la voz. */
  inspirar(prerollMs: number): void;
}

export interface DependenciasSesion {
  ws: WsSesion;
  api: ApiSesion;
  obtenerToken: () => Promise<string | null>;
  obtenerMicrofono: () => Promise<MediaStream>;
  crearGrabador: (stream: MediaStream) => Grabador;
  reproductor: Reproductor;
  crearContextoAudio: () => ContextoAudio;
  verificarAudio?: (audio: AudioDeclarado) => void;
  reloj?: Reloj;
  /**
   * Tiempos de la vida del avatar que dependen del turno (tanda 8): la
   * retención y el decay. Default: los del código.
   */
  vida?: Readonly<VidaOpts>;
  capasVida?: Readonly<CapasVida>;
  // Conexión con el store
  dispatch: (evento: EventoSesion) => void;
  getEstado: () => EstadoSesion;
  registrar: (tipo: EntradaDiagnostico['tipo'], nombre: string, datos?: unknown) => void;
  advertir?: (mensaje: string) => void;
}

type Timer =
  | 'conexion'
  | 'reconexion'
  | 'respuesta'
  | 'audio'
  | 'tope'
  | 'aviso'
  /** Retención de la expresión antes del decay a `NEUTRAL` (tanda 8). */
  | 'retencion'
  /** Pre-roll antes de reproducir la respuesta (tanda 8). */
  | 'preroll';

const RELOJ_REAL: Reloj = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (id) => globalThis.clearTimeout(id as ReturnType<typeof setTimeout>),
};

/**
 * Orquestador de la sesión de entrevista (`04_ARQUITECTURA.md` §8.1). Es
 * el único lugar con efectos: socket, micrófono, reproducción, avatar y
 * temporizadores. Todo lo que pasa afuera se traduce a eventos de
 * `sesionMachine`; las decisiones de "qué se puede hacer" se toman allá.
 *
 * Una instancia por pantalla de sesión. `destruir()` libera todo.
 */
export class OrquestadorSesion {
  private readonly reloj: Reloj;
  private readonly vida: Readonly<VidaOpts>;
  private readonly capasVida: Readonly<CapasVida>;
  private readonly timers = new Map<Timer, unknown>();
  private readonly desuscribir: (() => void)[] = [];
  private suscrito = false;

  private contexto: ContextoAudio | null = null;
  private stream: MediaStream | null = null;
  private grabador: Grabador | null = null;
  private inicioGrabacion: Promise<void> | null = null;
  private secuencia = 0;
  private simulacionIdConocido: string | null = null;
  /** El próximo `audio.binario` es de un backchannel, no del turno. */
  private proximoBinarioEsBackchannel = false;
  /** Invalida el fin de una reproducción cortada por otra más nueva. */
  private reproduccionActual = 0;
  private audioPendiente: WsAudioBinarioPayload | null = null;
  private avatar: ControlAvatar | null = null;
  /** Lo último que se le dijo al avatar sobre "pensando". */
  private avatarPensando = false;
  private ultimoAvisoId: number | null = null;
  /** Respuesta de `mi-simulacion` con la que se inició: la usa el reintento como sesión nueva. */
  private miSimulacion: MiSimulacionResponse | null = null;
  private yaReintentoComoNueva = false;

  constructor(private readonly deps: DependenciasSesion) {
    this.reloj = deps.reloj ?? RELOJ_REAL;
    this.vida = deps.vida ?? VIDA_DEFECTOS;
    this.capasVida = deps.capasVida ?? CAPAS_VIDA_DEFECTOS;
  }

  /** El avatar carga después que la pantalla: se engancha cuando está listo. */
  conectarAvatar(control: ControlAvatar | null): void {
    this.avatar = control;
    this.deps.registrar('cliente', 'avatar', { conectado: control !== null });
    // Un avatar que llega a mitad de turno tiene que saber en qué está.
    this.avatarPensando = false;
    this.sincronizarPensando();
  }

  /** Avisa al avatar si el turno entró o salió de proceso. */
  private sincronizarPensando(): void {
    const pensando = turnoEnProceso(this.deps.getEstado());
    if (pensando === this.avatarPensando) return;
    this.avatarPensando = pensando;
    this.avatar?.pensar(pensando);
  }

  /** Aplica una expresión al avatar y deja rastro en el diagnóstico, se haya aplicado o no. */
  private aplicarExpresion(expresion: string, duracionMs?: number): void {
    this.deps.registrar('cliente', 'avatar.expresion', {
      expresion,
      aplicada: this.avatar !== null,
      ...(duracionMs !== undefined ? { duracion_ms: duracionMs } : {}),
    });
    this.avatar?.aplicarExpresion(expresion, duracionMs);
  }

  /**
   * Al terminar de sonar el audio, la expresión se sostiene
   * `retencionMs` y recién después vuelve a `NEUTRAL` en `decaimientoMs`
   * (tanda 8, pieza 4). Si en el medio llega otra respuesta, su expresión
   * manda y el decay se cancela.
   *
   * No toca el re-armado del PTT: ese depende de `paciente.hablando_fin`,
   * del audio y de que terminó de sonar, y se resuelve en el reducer.
   */
  private volverANeutral(): void {
    if (!this.capasVida.decaimiento) {
      this.aplicarExpresion('NEUTRAL');
      return;
    }
    const { retencionMs, decaimientoMs } = this.vida;
    this.programar('retencion', retencionMs, () => {
      this.aplicarExpresion('NEUTRAL', decaimientoMs);
    });
  }

  /**
   * Calcula la envolvente del audio que acaba de empezar a sonar y se la
   * pasa al avatar para que mueva la boca (tanda 4, D-4).
   *
   * Corre **en paralelo** a la reproducción y no la condiciona: si no hay
   * contexto de audio, si el navegador no decodifica el formato o si
   * mientras tanto empezó otro audio, el paciente habla igual con la boca
   * quieta (P-3).
   */
  private async prepararHabla(
    b: WsAudioBinarioPayload,
    idReproduccion: number,
    inicioMs: number,
  ): Promise<void> {
    if (!this.contexto || !this.avatar) return;
    const envolvente = await envolventeDeAudio(b.data, this.contexto);
    if (idReproduccion !== this.reproduccionActual) return;
    if (!envolvente) {
      this.deps.registrar('cliente', 'habla_sin_envolvente', { mime_type: b.mime_type });
      return;
    }
    this.avatar?.hablar(envolvente, Date.now() - inicioMs);
  }

  // ---------- Inicio ----------

  /**
   * "Iniciar entrevista" o "Retomar entrevista". **Debe llamarse
   * directamente desde el handler del clic**: crea y reanuda el contexto de
   * audio dentro del gesto, lo que desbloquea la reproducción (D-8).
   */
  iniciar(miSimulacion: MiSimulacionResponse): void {
    this.dispatch({ tipo: 'INICIAR' });
    if (this.deps.getEstado().fase !== 'iniciando') return;
    if (!this.contexto) this.contexto = this.deps.crearContextoAudio();
    void this.contexto.resume().catch(() => undefined);
    void this.iniciarAsync(miSimulacion);
  }

  private async iniciarAsync(ms: MiSimulacionResponse): Promise<void> {
    this.miSimulacion = ms;
    const verificar = this.deps.verificarAudio ?? verificarAudioDeclarado;
    try {
      verificar(ms.audio);
    } catch (error) {
      this.deps.registrar('cliente', 'audio_declarado_no_soportado', {
        code: (error as AudioDeclaradoError).code,
        mime_type: (error as AudioDeclaradoError).mime_type,
      });
      this.dispatch({ tipo: 'AUDIO_NO_SOPORTADO' });
      return;
    }

    try {
      if (!this.stream) this.stream = await this.deps.obtenerMicrofono();
      if (!this.grabador) this.grabador = this.deps.crearGrabador(this.stream);
    } catch (error) {
      this.deps.registrar('cliente', 'microfono_denegado', { error: String(error) });
      this.dispatch({ tipo: 'INICIO_FALLIDO', motivo: 'microfono' });
      return;
    }

    const token = await this.deps.obtenerToken();
    const tenantId = token !== null ? tenantIdDelToken(token) : null;
    if (tenantId === null) {
      this.dispatch({ tipo: 'INICIO_FALLIDO', motivo: 'token' });
      return;
    }

    this.suscribir();
    const retomar = this.deps.getEstado().modo === 'retomar';
    let args: ConnectArgs;
    if (retomar) {
      this.simulacionIdConocido = ms.simulacion.id;
      args = { simulacionId: ms.simulacion.id, tenantId, modalidad: ms.simulacion.modalidad };
    } else {
      try {
        const inicio = await this.deps.api.iniciar({ asignacionId: ms.asignacion.id });
        this.simulacionIdConocido = inicio.simulacion_id;
        this.deps.registrar('cliente', 'iniciar', {
          simulacion_id: inicio.simulacion_id,
          modalidad: inicio.modalidad,
          avatar_id: inicio.avatar_id,
        });
        args = { ticketRef: inicio.ticket, tenantId, modalidad: inicio.modalidad };
      } catch (error) {
        this.deps.registrar('cliente', 'iniciar_fallido', { error: String(error) });
        this.dispatch({ tipo: 'INICIO_FALLIDO', motivo: 'iniciar' });
        return;
      }
    }
    await this.conectar(args, true);
  }

  private async conectar(args: ConnectArgs, primera: boolean): Promise<void> {
    this.deps.ws.disconnect();
    if (primera) {
      this.programar('conexion', ESPERA_CONEXION_MS, () => {
        this.deps.ws.disconnect();
        this.dispatch({ tipo: 'TIMEOUT_CONEXION' });
      });
    }
    try {
      await this.deps.ws.connect(args);
    } catch (error) {
      this.cancelar('conexion');
      this.deps.registrar('cliente', 'connect_fallido', { error: String(error) });
      this.dispatch(
        primera ? { tipo: 'INICIO_FALLIDO', motivo: 'token' } : { tipo: 'RECONEXION_FALLIDA' },
      );
    }
  }

  /**
   * "Retomar" recibió `ticket_invalido`: `mi-simulacion` dijo
   * `reconexion: true` pero el gateway no tiene historial que retomar (visto
   * contra MVP el 2026-09-19, tras un reset desde el lab). Se pide un ticket
   * nuevo, **una sola vez**, sin mostrar el error: es la acción que JC §0.5
   * prescribe para ese código, hecha sin pedirle un clic al alumno.
   * Devuelve `true` si tomó el caso.
   */
  private reintentarComoNueva(codigo: string): boolean {
    const s = this.deps.getEstado();
    if (
      codigo !== 'ticket_invalido' ||
      s.fase !== 'iniciando' ||
      s.modo !== 'retomar' ||
      this.yaReintentoComoNueva ||
      this.miSimulacion === null
    ) {
      return false;
    }
    this.yaReintentoComoNueva = true;
    this.deps.registrar('cliente', 'retomar_fallido_reintento_como_nueva');
    this.deps.ws.disconnect();
    this.dispatch({ tipo: 'RETOMAR_FALLIDO' });
    void this.iniciarAsync(this.miSimulacion);
    return true;
  }

  /**
   * Respaldo de `reconnection_failed`: si en `ESPERA_RECONEXION_MS` no volvió
   * el ack de `conectar`, se corta el socket y se ofrece "Reintentar". Así
   * "Reconectando…" no queda colgado aunque Socket.IO no avise.
   */
  private vigilarReconexion(): void {
    this.programar('reconexion', ESPERA_RECONEXION_MS, () => {
      if (this.deps.getEstado().fase !== 'reconectando') return;
      this.deps.registrar('cliente', 'reconexion_vencida');
      this.deps.ws.disconnect();
      this.dispatch({ tipo: 'RECONEXION_FALLIDA' });
    });
  }

  /** "Reintentar" tras `reconnection_failed` (D-10). */
  async reintentarConexion(): Promise<void> {
    this.dispatch({ tipo: 'REINTENTAR_CONEXION' });
    if (this.deps.getEstado().fase !== 'reconectando') return;
    this.deps.ws.disconnect();
    this.vigilarReconexion();
    try {
      const ms = await this.deps.api.getMiSimulacion();
      this.deps.registrar('cliente', 'mi_simulacion', {
        reconexion: ms.reconexion,
        simulacion_id: ms.simulacion.id,
      });
      if (!ms.reconexion) {
        this.dispatch({ tipo: 'SESION_ERROR', codigo: 'sin_simulacion_en_curso' });
        return;
      }
      const token = await this.deps.obtenerToken();
      const tenantId = token !== null ? tenantIdDelToken(token) : null;
      if (tenantId === null) {
        this.dispatch({ tipo: 'SESION_ERROR', codigo: 'no_autenticado' });
        return;
      }
      this.simulacionIdConocido = ms.simulacion.id;
      this.suscribir();
      await this.conectar(
        { simulacionId: ms.simulacion.id, tenantId, modalidad: ms.simulacion.modalidad },
        false,
      );
    } catch (error) {
      this.deps.registrar('cliente', 'reintento_fallido', { error: String(error) });
      this.dispatch({ tipo: 'RECONEXION_FALLIDA' });
    }
  }

  /** Volver al estado inicial (p. ej. `ticket_invalido` → pedir otro ticket). */
  reiniciar(modo: 'nuevo' | 'retomar'): void {
    this.cancelarTodos();
    this.deps.ws.disconnect();
    this.dispatch({ tipo: 'REINICIAR', modo });
  }

  // ---------- Socket ----------

  private suscribir(): void {
    if (this.suscrito) return;
    this.suscrito = true;
    const { ws, registrar } = this.deps;
    this.desuscribir.push(
      ws.on('connected', (ack) => {
        this.cancelar('conexion');
        this.cancelar('reconexion');
        registrar('servidor', 'connected', ack);
        this.dispatch({ tipo: 'CONECTADO', simulacionId: ack.simulacion_id });
      }),
      ws.on('disconnected', (reason) => {
        registrar('servidor', 'disconnected', { reason });
        // El corte que hace el propio cliente no es una caída.
        if (reason === 'io client disconnect') return;
        this.dispatch({ tipo: 'DESCONECTADO' });
        if (this.deps.getEstado().fase === 'reconectando') this.vigilarReconexion();
      }),
      ws.on('reconnection_failed', () => {
        this.cancelar('reconexion');
        registrar('servidor', 'reconnection_failed');
        this.dispatch({ tipo: 'RECONEXION_FALLIDA' });
      }),
      ws.on('paciente_hablando_inicio', (p) => {
        registrar('servidor', 'paciente.hablando_inicio', p);
        this.dispatch({ tipo: 'PACIENTE_HABLANDO_INICIO' });
      }),
      ws.on('audio_paciente', (p) => {
        // Ya pasó por el borde: `ia` sin `behavioral_cues` (D-13).
        registrar('servidor', 'audio.generado_por_paciente', p);
        this.proximoBinarioEsBackchannel = false;
        // D-5: la expresión se aplica antes de que suene el audio. Si la
        // anterior todavía se estaba sosteniendo, la nueva la interrumpe.
        this.cancelar('retencion');
        // Pre-roll (tanda 8, pieza 5): una transición de entrada más corta
        // que la del motor, para que la cara esté puesta cuando arranque la
        // voz.
        this.aplicarExpresion(
          p.ia.expresion_avatar,
          this.capasVida.preroll ? this.vida.transicionEntradaMs : undefined,
        );
        this.dispatch({
          tipo: 'AUDIO_PACIENTE',
          alumno: p.alumno_transcripcion,
          paciente: p.ia.patient_response,
          expresion: p.ia.expresion_avatar,
        });
      }),
      ws.on('audio_backchannel', (p) => {
        registrar('servidor', 'audio.backchannel_generado_por_paciente', p);
        this.proximoBinarioEsBackchannel = true;
      }),
      ws.on('audio_binario', (b) => {
        registrar('servidor', 'audio.binario', {
          mime_type: b.mime_type,
          bytes: b.data.byteLength,
        });
        if (this.proximoBinarioEsBackchannel) {
          this.proximoBinarioEsBackchannel = false;
          void this.deps.reproductor.play(b.data, b.mime_type).catch(() => undefined);
          return;
        }
        this.reproducir(b, false);
      }),
      ws.on('paciente_hablando_fin', (p) => {
        registrar('servidor', 'paciente.hablando_fin', p);
        this.cancelar('respuesta');
        this.dispatch({ tipo: 'PACIENTE_HABLANDO_FIN' });
        const s = this.deps.getEstado();
        if (s.turno.etapa === 'esperando' && !s.turno.audioRecibido) {
          const numero = s.turno.numero;
          this.programar('audio', ESPERA_AUDIO_PACIENTE_MS, () =>
            this.dispatch({ tipo: 'TIMEOUT_AUDIO', turno: numero }),
          );
        }
      }),
      ws.on('turno_fallido', (p) => {
        registrar('servidor', 'turno.fallido', p);
        this.cancelar('respuesta');
        this.cancelar('audio');
        this.dispatch({ tipo: 'TURNO_FALLIDO' });
      }),
      ws.on('sesion_error', (p) => {
        registrar('servidor', 'sesion.error', p);
        this.cancelarTodos();
        if (this.reintentarComoNueva(p.codigo)) return;
        this.detenerMedios();
        this.dispatch({ tipo: 'SESION_ERROR', codigo: p.codigo });
      }),
      ws.on('validation_error', (evento, issues) => {
        registrar('servidor', 'validation_error', { evento, issues });
      }),
    );
  }

  // ---------- Reproducción ----------

  /**
   * Reproduce la respuesta del paciente. Con pre-roll (tanda 8, pieza 5),
   * el audio arranca `prerollMs` después de llegar y el avatar toma aire
   * en el final de ese tramo. El estado pasa a "reproduciendo" en el acto,
   * así que el PTT no se arma durante el pre-roll; el re-armado sigue
   * dependiendo de que el audio termine de sonar.
   *
   * La reproducción manual ("Reproducir", D-8) no lleva pre-roll: viene de
   * un clic y se respeta el gesto tal cual.
   */
  private reproducir(b: WsAudioBinarioPayload, manual: boolean): void {
    this.cancelar('audio');
    this.cancelar('preroll');
    this.audioPendiente = b;
    const id = ++this.reproduccionActual;
    this.dispatch(manual ? { tipo: 'REPRODUCCION_MANUAL' } : { tipo: 'AUDIO_RECIBIDO' });
    const prerollMs = this.capasVida.preroll && !manual ? this.vida.prerollMs : 0;
    if (prerollMs <= 0) {
      this.sonar(b, id);
      return;
    }
    this.avatar?.inspirar(prerollMs);
    this.programar('preroll', prerollMs, () => {
      if (id === this.reproduccionActual) this.sonar(b, id);
    });
  }

  private sonar(b: WsAudioBinarioPayload, id: number): void {
    const inicioMs = Date.now();
    this.deps.reproductor.play(b.data, b.mime_type).then(
      () => {
        if (id !== this.reproduccionActual) return;
        this.audioPendiente = null;
        this.avatar?.dejarDeHablar();
        // D-5 de la tanda 2: al terminar, vuelve a neutro; desde la tanda
        // 8, después de sostenerla un rato.
        this.volverANeutral();
        this.dispatch({ tipo: 'REPRODUCCION_TERMINADA' });
      },
      (error: unknown) => {
        if (id !== this.reproduccionActual) return;
        this.avatar?.dejarDeHablar();
        this.deps.registrar('cliente', 'reproduccion_bloqueada', { error: String(error) });
        this.dispatch({ tipo: 'REPRODUCCION_BLOQUEADA' });
      },
    );
    void this.prepararHabla(b, id, inicioMs);
  }

  /** Botón "Reproducir" cuando el autoplay falló (D-8). Va dentro de un clic. */
  reproducirPendiente(): void {
    if (!this.audioPendiente || !this.deps.getEstado().reproduccionBloqueada) return;
    void this.contexto?.resume().catch(() => undefined);
    this.reproducir(this.audioPendiente, true);
  }

  // ---------- Turno del alumno ----------

  /** Clic en el PTT: empieza o termina la grabación según el estado (D-6). */
  pttPulsado(): void {
    const s = this.deps.getEstado();
    if (pttArmado(s)) this.empezarGrabacion();
    else if (pttGrabando(s)) void this.terminarGrabacion();
  }

  private empezarGrabacion(): void {
    const grabador = this.grabador;
    if (!grabador) return;
    this.dispatch({ tipo: 'PTT_INICIAR' });
    this.inicioGrabacion = grabador.start();
    this.inicioGrabacion.then(
      () => this.programar('tope', TOPE_GRABACION_MS, () => void this.terminarGrabacion()),
      (error: unknown) => {
        this.deps.registrar('cliente', 'grabacion_fallida', { error: String(error) });
        this.dispatch({ tipo: 'GRABACION_FALLIDA' });
      },
    );
  }

  private async terminarGrabacion(): Promise<void> {
    if (!pttGrabando(this.deps.getEstado()) || !this.grabador) return;
    this.cancelar('tope');
    this.dispatch({ tipo: 'PTT_DETENER' });

    let resultado: AudioRecorderResult;
    try {
      await this.inicioGrabacion;
      resultado = await this.grabador.stop();
    } catch (error) {
      this.deps.registrar('cliente', 'grabacion_fallida', { error: String(error) });
      this.dispatch({ tipo: 'GRABACION_FALLIDA' });
      return;
    }

    let rms: number | null = null;
    try {
      if (this.contexto) rms = await medirRms(this.contexto, resultado.binary);
    } catch (error) {
      // Si no se puede medir, se envía igual: mejor un turno de más que
      // tragarse lo que dijo la persona.
      this.deps.registrar('cliente', 'rms_no_medible', { error: String(error) });
    }

    const { metadata } = resultado;
    const datos = {
      mime_type: metadata.mime_type,
      duracion_audio_segundos: metadata.duracion_audio_segundos,
      bytes: resultado.binary.byteLength,
      rms,
    };
    if (rms !== null && rms < UMBRAL_RMS_SILENCIO) {
      this.deps.registrar('cliente', 'audio_descartado', datos);
      this.dispatch({ tipo: 'GRABACION_DESCARTADA' });
      return;
    }

    const s = this.deps.getEstado();
    const simulacionId = s.simulacionId ?? this.simulacionIdConocido;
    if (s.fase !== 'en_curso' || s.turno.etapa !== 'procesando' || simulacionId === null) {
      // La conexión cayó mientras se procesaba: el turno se pierde; el PTT
      // se re-arma al reconectar.
      this.deps.registrar('cliente', 'turno_no_enviado', { fase: s.fase });
      return;
    }
    try {
      this.secuencia += 1;
      this.deps.ws.emitAudioGeneradoPorAlumno(
        {
          ...metadata,
          simulacion_id: simulacionId,
          secuencia: this.secuencia,
          ...(rms !== null ? { rms_cliente: rms } : {}),
        },
        resultado.binary,
      );
    } catch (error) {
      this.deps.registrar('cliente', 'envio_fallido', { error: String(error) });
      this.dispatch({ tipo: 'GRABACION_FALLIDA' });
      return;
    }
    this.deps.registrar('cliente', 'audio.generado_por_alumno', {
      ...datos,
      secuencia: this.secuencia,
      audio_ref_id: metadata.audio_ref_id,
    });
    this.dispatch({ tipo: 'TURNO_ENVIADO' });
    const numero = this.deps.getEstado().turno.numero;
    this.programar('respuesta', ESPERA_RESPUESTA_MS, () =>
      this.dispatch({ tipo: 'TIMEOUT_RESPUESTA', turno: numero }),
    );
  }

  // ---------- Cierre (D-12) ----------

  solicitarCierre(): void {
    this.dispatch({ tipo: 'CIERRE_SOLICITADO' });
  }

  cancelarCierre(): void {
    this.dispatch({ tipo: 'CIERRE_CANCELADO' });
  }

  /** Confirmación: entrevista.finalizada → closeSession → POST /finalizar. */
  async confirmarCierre(): Promise<void> {
    this.dispatch({ tipo: 'CIERRE_CONFIRMADO' });
    if (this.deps.getEstado().fase !== 'finalizando') return;
    this.cancelarTodos();
    this.detenerMedios();
    const { ws, registrar } = this.deps;
    try {
      ws.emitEntrevistaFinalizadaPorAlumno();
    } catch (error) {
      registrar('cliente', 'entrevista_finalizada_no_enviada', { error: String(error) });
    }
    let cerradoPorServidor = false;
    try {
      ({ cerradoPorServidor } = await ws.closeSession());
    } catch (error) {
      registrar('cliente', 'close_session_fallido', { error: String(error) });
    }
    registrar('cliente', 'close_session', { cerradoPorServidor });
    if (!cerradoPorServidor) {
      (this.deps.advertir ?? console.warn)(
        'closeSession: el servidor no cerró el socket dentro del tope; se sigue con /finalizar',
      );
    }
    await this.finalizarRest();
  }

  /** Reintento de `/finalizar` tras `cierre_fallido`. El socket ya está cerrado. */
  async reintentarCierre(): Promise<void> {
    this.dispatch({ tipo: 'REINTENTAR_CIERRE' });
    if (this.deps.getEstado().fase !== 'finalizando') return;
    await this.finalizarRest();
  }

  private async finalizarRest(): Promise<void> {
    const simulacionId = this.deps.getEstado().simulacionId ?? this.simulacionIdConocido;
    if (simulacionId === null) {
      this.dispatch({ tipo: 'CIERRE_FALLIDO', motivo: 'sin_simulacion' });
      return;
    }
    try {
      const r = await this.deps.api.finalizar({ simulacion_id: simulacionId, motivo: 'alumno' });
      this.deps.registrar('cliente', 'finalizar', r);
      this.liberarMicrofono();
      this.dispatch({ tipo: 'FINALIZADA' });
    } catch (error) {
      this.deps.registrar('cliente', 'finalizar_fallido', { error: String(error) });
      this.dispatch({ tipo: 'CIERRE_FALLIDO', motivo: 'finalizar' });
    }
  }

  // ---------- Limpieza ----------

  /** Salir de la pantalla: libera micrófono, audio, timers y socket. */
  destruir(): void {
    this.cancelarTodos();
    for (const off of this.desuscribir.splice(0)) off();
    this.suscrito = false;
    this.deps.ws.disconnect();
    this.detenerMedios();
    this.liberarMicrofono();
    void this.contexto?.close().catch(() => undefined);
    this.contexto = null;
    this.avatar = null;
  }

  private detenerMedios(): void {
    this.cancelar('preroll');
    this.reproduccionActual += 1;
    this.deps.reproductor.stop();
    this.avatar?.dejarDeHablar();
  }

  private liberarMicrofono(): void {
    this.grabador?.dispose();
    this.grabador = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  // ---------- Timers ----------

  private dispatch(evento: EventoSesion): void {
    this.deps.dispatch(evento);
    this.sincronizarPensando();
    // Cada aviso nuevo se borra solo a los 5 s (D-10).
    const aviso = this.deps.getEstado().aviso;
    if (aviso && aviso.id !== this.ultimoAvisoId) {
      this.ultimoAvisoId = aviso.id;
      const id = aviso.id;
      this.programar('aviso', DURACION_AVISO_MS, () =>
        this.deps.dispatch({ tipo: 'AVISO_EXPIRADO', id }),
      );
    }
  }

  private programar(timer: Timer, ms: number, fn: () => void): void {
    this.cancelar(timer);
    this.timers.set(
      timer,
      this.reloj.setTimeout(() => {
        this.timers.delete(timer);
        fn();
      }, ms),
    );
  }

  private cancelar(timer: Timer): void {
    if (!this.timers.has(timer)) return;
    this.reloj.clearTimeout(this.timers.get(timer));
    this.timers.delete(timer);
  }

  private cancelarTodos(): void {
    for (const timer of [...this.timers.keys()]) this.cancelar(timer);
  }
}
